import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, PaperPlaneTilt, ChatCircleDots, ArrowClockwise, Phone, Calendar } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m the Total Wireless Repair assistant. How can I help you today? I can answer questions about repairs, pricing, store hours, or help you book a repair.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chat`, { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I\'m having trouble connecting. Please call us at (248) 677-1040 for immediate help.' }]);
    }
    setLoading(false);
  };

  const quickActions = [
    { label: 'Repair Pricing', msg: 'What are your repair prices?' },
    { label: 'Store Hours', msg: 'What are your store hours?' },
    { label: 'Book Repair', msg: 'I\'d like to book a repair' },
  ];

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-[#E3000F] hover:bg-[#FF3B30] text-white rounded-full shadow-lg shadow-[#E3000F]/30 flex items-center justify-center transition-all hover:scale-110"
        data-testid="chat-widget-toggle">
        <ChatCircleDots size={28} weight="fill" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 w-[380px] h-[520px] bg-[#111111] border border-[#262626] shadow-2xl shadow-black/50 flex flex-col" data-testid="chat-widget">
      {/* Header */}
      <div className="bg-[#E3000F] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={20} className="text-white" weight="fill" />
          <div>
            <p className="text-white text-sm font-bold">Repair Assistant</p>
            <p className="text-white/70 text-[10px]">Typically replies instantly</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X size={18} /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
              m.role === 'user'
                ? 'bg-[#E3000F] text-white rounded-t-lg rounded-bl-lg'
                : 'bg-[#1A1A1A] text-[#E5E5E5] border border-[#262626] rounded-t-lg rounded-br-lg'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#1A1A1A] border border-[#262626] px-4 py-2 rounded-t-lg rounded-br-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-[#525252] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-[#525252] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-[#525252] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {quickActions.map(a => (
            <button key={a.label} onClick={() => { setInput(a.msg); }}
              className="text-[10px] border border-[#262626] text-[#A3A3A3] px-2.5 py-1 hover:border-[#E3000F] hover:text-[#E3000F] transition-colors"
              data-testid={`chat-quick-${a.label.replace(/\s/g, '-').toLowerCase()}`}>
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-[#262626] flex items-center gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message..."
          className="flex-1 bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#E3000F]"
          data-testid="chat-input" />
        <button onClick={send} disabled={loading || !input.trim()}
          className="bg-[#E3000F] hover:bg-[#FF3B30] disabled:opacity-50 text-white p-2 transition-colors"
          data-testid="chat-send">
          <PaperPlaneTilt size={18} weight="fill" />
        </button>
      </div>
    </div>
  );
}
